import React, { useState } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { countTags } from '@btag/bt-personalization';

import handleKeyPress from '../../../common/handleKeyPress';
import eventService from '../../../common/eventService';
import areBlocksEqual from '../../../common/areBlocksEqual';
import Feedback from './Feedback';
import * as htmlOutputActions from '../../../actions/htmlOutput';
import MultipleUsersModal from './MultipleUsersModal';
import ConnectedUsers from './ConnectedUsers';
import Button from '../../../componentLibrary/Button';
import BananatagButton from '../../../componentLibrary/BananatagButton';
import { blockType } from '../../../types';
import * as Styled from './styles';
import { APPLICATION_MODES, DRAFT_ACCESS } from '../../../constants/itemTypes/itemTypes';
import PdfModal from './DownloadModals/PdfModal';
import HtmlModal from './DownloadModals/HtmlModal';
import { request } from '../../../common/request';
import { ERR_HTML_GENERATION } from '../../../constants/Errors';
import CustomTooltip from '../../../componentLibrary/CustomTooltip';
import './Download.css';

const DropDown = ({ dropDownItems, toggleDropDown }) => (
    <Styled.DropDown role="listbox">
        {dropDownItems.map(
            (item, key) =>
                item.hasControl && (
                    <Styled.DropDownItem
                        hasBorder={item.hasBorder}
                        key={item.name}
                        className="downloadButton"
                        onClick={item.function}
                        onKeyDown={(e) => handleKeyPress(e, item.function)}
                        role="button"
                        tabIndex="0"
                        aria-label={item.name}
                        onBlur={() => key === dropDownItems.length - 1 && toggleDropDown(false)}
                    >
                        {item.name}
                    </Styled.DropDownItem>
                )
        )}
    </Styled.DropDown>
);

DropDown.propTypes = {
    dropDownItems: PropTypes.array.isRequired,
    toggleDropDown: PropTypes.func.isRequired,
};

const TopNavRightSideActions = (props) => {
    const {
        setApplicationMode,
        applicationMode,
        isVersionHistoryItemCurrent,
        connectedClients,
        dispatch,
        blocks,
        htmlBody,
        draftID,
        userPlan,
        controls,
        hasDesignAccess,
        hasPersonalization,
        sessionFeatures,
    } = props;

    const [showPDFModal, setShowPDFModal] = useState(false);
    const togglePdfModal = () => setShowPDFModal(!showPDFModal);

    const [showHtmlModal, setShowHtmlModal] = useState(false);
    const toggleHtmlModal = () => setShowHtmlModal(!showHtmlModal);

    const [feedbackDialogueOpen, setFeedbackDialogueOpen] = useState(false);
    const [dropDownType, setDropDownType] = useState('none');
    const [showDropDown, setShowDropDown] = useState(false);

    const { ed484PersonalizationMsft } = useFlags();

    const {
        F_SEND_TEST_EMAIL,
        F_DOWNLOAD_PDF,
        F_DOWNLOAD_HTML,
        F_SAVE_AS_TEMPLATE,
        F_TUTORIALS,
        F_FEEDBACK,
        F_SHARE_DRAFT,
    } = sessionFeatures;

    const multipleUsersConnected = connectedClients.length > 0;
    const toggleDropDown = (state) => {
        setShowDropDown(state);
    };

    const getHtmlBody = async (next) => {
        try {
            const { html } = await request.convertHtml({draftID, blocks});
            dispatch(htmlOutputActions.updatedHtmlBody({ html }));
            next(null, html);
        } catch (err) {
            next(err);
        }
    };

    const transmitHtmlBody = (purpose) => {
        getHtmlBody((err, html) => {
            if (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                eventService.notify(err.customMessage ? err.customMessage : ERR_HTML_GENERATION, true);
                return;
            }

            const blocksData = JSON.stringify(blocks);
            eventService.sendHtml(html, purpose, blocksData);
        });
    };

    const saveAsTemplate = () => {
        transmitHtmlBody('saveAsTemplate');
    };

    const sendTestEmail = () => {
        transmitHtmlBody('sendTest');
    };

    const toggleFeedbackModal = () => {
        setDropDownType('none');
        setFeedbackDialogueOpen(!feedbackDialogueOpen);
    };

    const openStaffbaseCampus = () => {
        window.open('https://campus.staffbase.com/', '_blank', 'noopener');
    };

    return (
        <Styled.WrapperDiv>
            <MultipleUsersModal
                connectedClients={connectedClients}
                multipleUsersConnected={multipleUsersConnected}
                userPlan={userPlan}
            />
            {showPDFModal && (
                <PdfModal
                    togglePdfModal={togglePdfModal}
                    showPersonalizationWarning={ed484PersonalizationMsft && hasPersonalization && !!countTags(htmlBody)}
                />
            )}

            {showHtmlModal && (
                <HtmlModal
                    toggleHtmlModal={toggleHtmlModal}
                    showPersonalizationWarning={ed484PersonalizationMsft && hasPersonalization && !!countTags(htmlBody)}
                />
            )}

            {applicationMode === APPLICATION_MODES.VERSION_HISTORY && (
                <Styled.ExitPreviewButtonDiv>
                    <BananatagButton
                        text="Exit Version History"
                        onClick={setApplicationMode(APPLICATION_MODES.EDITOR)}
                        type={isVersionHistoryItemCurrent ? 'solid' : 'outline'}
                        size="medium"
                    />
                </Styled.ExitPreviewButtonDiv>
            )}

            {applicationMode !== APPLICATION_MODES.VERSION_HISTORY && (
                <>
                    {(!F_SHARE_DRAFT || controls.includes('shareDraft')) && (
                        <Styled.ButtonDiv
                            data-tip
                            data-for="shareDraftButton"
                            id="share-draft"
                            onFocus={(e) => {
                                e.stopPropagation();
                                toggleDropDown(false);
                            }}
                        >
                            <Button
                                type="simple"
                                icon="user-plus"
                                width={30}
                                iconSize={17}
                                color={!F_SHARE_DRAFT ? '#CCCCCC' : '#535454'}
                                cursor={!F_SHARE_DRAFT ? 'not-allowed' : 'pointer'}
                                hasTooltip={F_SHARE_DRAFT}
                                hasHoverEffects={F_SHARE_DRAFT}
                                label="Invite Collaborators"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (F_SHARE_DRAFT) {
                                        eventService.shareDraft();
                                    }
                                }}
                            />
                        </Styled.ButtonDiv>
                    )}
                    {!F_SHARE_DRAFT && (
                        <CustomTooltip
                            dataRef="shareDraftButton"
                            offset={{ top: 13, right: -20 }}
                            text="Learn how to share drafts (edit or view only) with a full product demo."
                        />
                    )}
                    {(F_TUTORIALS || F_FEEDBACK) && (
                        <Styled.ButtonDiv
                            onMouseOver={(e) => {
                                e.stopPropagation();
                                toggleDropDown(true);
                                setDropDownType('help');
                            }}
                            onMouseLeave={(e) => {
                                e.stopPropagation();
                                toggleDropDown(false);
                            }}
                            onFocus={(e) => {
                                e.stopPropagation();
                                toggleDropDown(true);
                                setDropDownType('help');
                            }}
                            role="combobox"
                            aria-controls="tutorials-feedback-dropdown"
                            aria-expanded={showDropDown && dropDownType === 'help'}
                            aria-labelledby="help-feedback-button"
                        >
                            <Button
                                id="help-feedback-button"
                                type="simple"
                                hasTooltip={false}
                                width={30}
                                iconSize={17}
                                label="help-feedback"
                                icon="question-circle"
                                color="#535454"
                            />
                            {dropDownType === 'help' && showDropDown && (
                                <DropDown
                                    id="tutorials-feedback-dropdown"
                                    dropDownItems={[
                                        {
                                            name: 'Tutorials',
                                            function: openStaffbaseCampus,
                                            hasControl: F_TUTORIALS,
                                        },
                                        {
                                            name: 'Feedback',
                                            function: toggleFeedbackModal,
                                            hasControl: F_FEEDBACK,
                                        },
                                    ]}
                                    toggleDropDown={toggleDropDown}
                                />
                            )}
                            <Feedback
                                open={feedbackDialogueOpen}
                                setOpen={(status) => setFeedbackDialogueOpen(status)}
                            />
                        </Styled.ButtonDiv>
                    )}
                    {(F_DOWNLOAD_PDF || F_DOWNLOAD_HTML || F_SAVE_AS_TEMPLATE) && (
                        <Styled.ButtonDiv
                            onMouseOver={(e) => {
                                e.stopPropagation();
                                toggleDropDown(true);
                                setDropDownType('download');
                            }}
                            onMouseLeave={(e) => {
                                e.stopPropagation();
                                toggleDropDown(false);
                            }}
                            onFocus={(e) => {
                                e.stopPropagation();
                                toggleDropDown(true);
                                setDropDownType('download');
                            }}
                            aria-controls="send-test-download-pdf-html-dropdown"
                            role="combobox"
                            aria-expanded={showDropDown && dropDownType === 'download'}
                            aria-labelledby="download-options-button"
                        >
                            <Button
                                id="download-options-button"
                                type="simple"
                                hasTooltip={false}
                                width={30}
                                iconSize={17}
                                label="download-options"
                                icon="share-square"
                                color="#535454"
                            />
                            {dropDownType === 'download' && showDropDown && (
                                <DropDown
                                    id="send-test-download-pdf-html-dropdown"
                                    dropDownItems={[
                                        {
                                            name: 'Send Test',
                                            function: sendTestEmail,
                                            hasControl: F_SEND_TEST_EMAIL && controls.includes('sendTest'),
                                        },
                                        {
                                            name: 'Download PDF',
                                            function: togglePdfModal,
                                            hasControl: F_DOWNLOAD_PDF && controls.includes('download'),
                                        },
                                        {
                                            name: 'Download HTML',
                                            function: toggleHtmlModal,
                                            hasControl: F_DOWNLOAD_HTML && controls.includes('download'),
                                        },
                                        {
                                            name: 'Save as New Template',
                                            function: saveAsTemplate,
                                            hasControl:
                                                F_SAVE_AS_TEMPLATE &&
                                                controls.includes('saveAsTemplate') &&
                                                hasDesignAccess,
                                            hasBorder: true,
                                        },
                                    ]}
                                    toggleDropDown={toggleDropDown}
                                />
                            )}
                        </Styled.ButtonDiv>
                    )}
                </>
            )}
            {multipleUsersConnected && (
                <>
                    <ConnectedUsers users={connectedClients} />
                </>
            )}
        </Styled.WrapperDiv>
    );
};

const mapStateToProps = (state) => {
    const access = _get(state, 'session.collaboration.access', DRAFT_ACCESS.OWNER);
    const integration = _get(state, 'session.integration', 'LOCALHOST');
    const isIntegrationsEnabled = _get(state, 'session.user.isIntegrationsEnabled', true);
    const hasDesignAccess = access !== DRAFT_ACCESS.READ;
    return {
        id: _get(state, 'session.collaboration.id', ''),
        userEmail: _get(state, 'session.user.email', 'N/A'),
        userPlan: _get(state, 'session.user.plan', 'IC_LEGACY'),
        htmlSize: _get(state, 'htmlOutput.size', 0),
        htmlBody: _get(state, 'htmlOutput.body', ''),
        draftID: _get(state, 'session.collaboration.id', ''),
        connectedClients: _get(state, 'session.collaboration.connectedClients', []).filter(
            (user) => user.socketId !== _get(state, 'session.collaboration.currentUserSocketId', '')
        ),
        isVersionHistoryItemCurrent: areBlocksEqual(
            state.versionHistory.versionHistoryItem.blocks,
            state.canvasBlocks.present
        ),
        controls: _get(state, 'session.controls', []),
        hasDesignAccess,
        sessionFeatures: _get(state, 'session.features', {}),
        hasPersonalization:
            _get(state, 'session.user.features.EMAIL_DESIGNER_PERSONALIZATION', false) &&
            (integration === 'LOCALHOST' || integration === 'APP') &&
            !isIntegrationsEnabled,
    };
};

TopNavRightSideActions.propTypes = {
    connectedClients: PropTypes.arrayOf(
        PropTypes.shape({
            draftId: PropTypes.string.isRequired,
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            email: PropTypes.string.isRequired,
        })
    ).isRequired,
    setApplicationMode: PropTypes.func.isRequired,
    applicationMode: PropTypes.string.isRequired,
    isVersionHistoryItemCurrent: PropTypes.bool.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    dispatch: PropTypes.func.isRequired,
    userPlan: PropTypes.string.isRequired,
    htmlBody: PropTypes.string.isRequired,
    draftID: PropTypes.string.isRequired,
    controls: PropTypes.arrayOf(PropTypes.string).isRequired,
    hasDesignAccess: PropTypes.bool.isRequired,
    hasPersonalization: PropTypes.bool.isRequired,
    sessionFeatures: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(TopNavRightSideActions);
